import { createElement, useState } from "react";
import { endpoints } from "@/lib/api";

export const levelLabels = { continent: "Continent", country: "Country", category: "Category",
    material: "Material / Subcategory", material_type: "Material type / Flow", process: "Process / Flow", parameter: "Parameter" };

export function buildSearchTree(results) {
    const roots = new Map();
    results.forEach(result => {
        let siblings = roots;
        result.path.forEach((node, index) => {
            const key = `${node.kind}:${node.id}`;
            if (!siblings.has(key)) siblings.set(key, { ...node, children: new Map() });
            const branch = siblings.get(key);
            if (index === result.path.length - 1) branch.match = result.match_type;
            siblings = branch.children;
        });
    });
    return [...roots.values()];
}

function Branch({ node, onOpen, options }) {
    const initial = [...node.children.values()];
    const [open, setOpen] = useState(initial.length > 0);
    const [children, setChildren] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const toggle = async () => {
        setError("");
        if (node.kind === "parameter") {
            setBusy(true);
            try { await onOpen(node); } catch { setError("Unable to open this parameter. Please retry."); }
            finally { setBusy(false); }
            return;
        }
        if (open) { setOpen(false); return; }
        if (!initial.length && children === null) {
            setBusy(true);
            try { setChildren(await endpoints.searchChildren(node.kind, node.id, options)); }
            catch { setError("Unable to load this branch. Click to retry."); return; }
            finally { setBusy(false); }
        }
        setOpen(true);
    };
    const visible = children || initial;
    return <li className="my-1">
        <button type="button" onClick={toggle} disabled={busy}
            aria-expanded={node.kind !== "parameter" ? open : undefined}
            className={`flex items-center gap-3 text-left w-full px-3 py-2 rounded hover:bg-violet-50 ${node.match ? "bg-violet-50/60" : ""}`}>
            <span aria-hidden="true">{busy ? "…" : node.kind === "parameter" ? "↗" : open ? "▾" : "▸"}</span>
            <span className={node.match ? "font-semibold text-violet-800" : "text-neutral-700"}>{node.flag} {node.name}</span>
            <span className="text-xs text-neutral-500">{levelLabels[node.kind]}</span>
            {node.match && <span className="text-xs text-violet-700 ml-auto">{options.collection ? "Curated" : `${node.match} match`}</span>}
        </button>
        {error && <p role="alert" className="text-red-700 text-sm px-6">{error}</p>}
        {open && <ul className="ml-5 pl-3 border-l border-neutral-200">
            {visible.map(child => createElement(Branch, { key: `${child.kind}:${child.id}`, node: { children: new Map(), ...child }, onOpen, options }))}
            {!visible.length && <li className="p-3 text-sm text-neutral-500">No entries below this node yet.</li>}
        </ul>}
    </li>;
}

function CompactResult({ result, onOpen, options, q }) {
    const [children, setChildren] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const isParameter = result.kind === "parameter";
    const explore = async () => {
        if (isParameter) { onOpen(result); return; }
        if (children) { setOpen(!open); return; }
        setLoading(true); setError("");
        try {
            const items = await endpoints.searchChildren(result.kind, result.id, options);
            setChildren(items.map(item => ({ ...item, path: [...result.path, item] })));
            setOpen(true);
        } catch { setError("Could not load this result. Please try again."); }
        finally { setLoading(false); }
    };
    const words = options.collection ? [] : q.trim().split(/\s+/).filter(w => w.length > 2);
    const label = result.name.split(/(\s+)/).map((part, i) => words.some(w => part.toLowerCase().includes(w.toLowerCase())) ? <mark key={i} className="bg-violet-100 text-violet-800 rounded px-0.5">{part}</mark> : part);
    return <li className="border border-neutral-200 rounded-xl bg-white overflow-hidden">
        <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <button onClick={explore} className="font-semibold text-neutral-900 text-left hover:text-violet-700">{label}</button>
                    <span className="text-[11px] rounded-full px-2 py-1 bg-violet-50 text-violet-700">{levelLabels[result.kind]}</span>
                    {options.collection && <span className="text-[11px] rounded-full px-2 py-1 bg-emerald-50 text-emerald-700">Curated</span>}
                </div>
                <p className="text-xs text-neutral-500 leading-6 mt-2">{result.path.slice(0,-1).map((node,i) => <span key={`${node.kind}:${node.id}`}>{i > 0 && <span aria-hidden="true" className="mx-2 text-neutral-300">›</span>}{node.name}</span>)}</p>
            </div>
            <button onClick={explore} disabled={loading} aria-expanded={isParameter ? undefined : open} className="shrink-0 text-sm text-violet-700 border border-violet-200 bg-violet-50/60 rounded-lg px-4 py-2 hover:bg-violet-100 disabled:opacity-50">{loading ? "Loading…" : isParameter ? "View data →" : open ? "Hide details ↑" : "Explore →"}</button>
        </div>
        {error && <p role="alert" className="text-sm text-red-700 px-4 pb-4">{error}</p>}
        {open && <ul className="p-3 pt-0 space-y-2 bg-neutral-50/50">{children?.map(child => createElement(CompactResult, { key: `${child.kind}:${child.id}`, result: child, onOpen, options, q }))}{!children?.length && <li className="p-3 text-sm text-neutral-500">No matching entries for these filters.</li>}</ul>}
    </li>;
}

export default function HierarchySearchResults({ q, data, onOpen, options = {} }) {
    const [page, setPage] = useState(data);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [view, setView] = useState("compact");
    const [country, setCountry] = useState("all");
    const [sort, setSort] = useState("relevance");
    const more = async () => {
        setBusy(true); setError("");
        try {
            const next = await endpoints.search(q, { ...options, offset: page.next_offset });
            setPage({ ...next, results: [...page.results, ...next.results] });
        } catch { setError("Could not load more results. Please retry."); }
        finally { setBusy(false); }
    };
    const groups = new Map();
    page.results.forEach(result => {
        const location = result.path.find(node => node.kind === "country") || result.path[0];
        if (!groups.has(location.id)) groups.set(location.id, { ...location, results: [] });
        groups.get(location.id).results.push(result);
    });
    const visible = page.results.filter(r => country === "all" || (r.path.find(n => n.kind === "country") || r.path[0]).id === country);
    const sorted = rows => sort === "name" ? [...rows].sort((a,b) => a.name.localeCompare(b.name)) : rows;
    return <div className="p-5 lg:px-8 lg:py-6 bg-[#FAFBFD] min-h-full">
        <div className="text-xs text-neutral-500 mb-4">Explore <span className="mx-2">›</span> {options.collection ? "Curated datasets" : "Search results"}</div>
        <h1 className="text-3xl font-semibold tracking-tight text-neutral-900 mb-2">{q}</h1>
        <p className="text-sm text-neutral-500 mb-6">{page.count} matching results · Explore parameters, materials, processes and their sources.</p>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <div className="flex flex-wrap gap-2" aria-label="Filter results by country">
                <button aria-pressed={country === "all"} onClick={() => setCountry("all")} className={`text-xs px-3 py-2 rounded-lg border ${country === "all" ? "bg-violet-700 text-white border-violet-700" : "bg-white border-neutral-200"}`}>All ({page.results.length})</button>
                {[...groups.values()].map(group => <button key={group.id} aria-pressed={country === group.id} onClick={() => setCountry(group.id)} className={`text-xs px-3 py-2 rounded-lg border ${country === group.id ? "bg-violet-700 text-white border-violet-700" : "bg-white border-neutral-200 text-neutral-600"}`}>{group.name} ({group.results.length})</button>)}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-600">
                <label>Sort by <select value={sort} onChange={e => setSort(e.target.value)} className="ml-2 border border-neutral-200 rounded-lg p-2 bg-white"><option value="relevance">Relevance</option><option value="name">Name A–Z</option></select></label>
                <div className="flex p-1 rounded-lg border border-neutral-200 bg-white" aria-label="Results view">{["compact", "tree"].map(mode => <button key={mode} onClick={() => setView(mode)} aria-pressed={view === mode} className={`px-3 py-2 rounded-md capitalize ${view === mode ? "bg-violet-700 text-white" : "text-neutral-500"}`}>{mode}</button>)}</div>
            </div>
        </div>
        {page.next_offset !== null && <p className="text-xs text-neutral-500 mb-4">Country counts and sorting apply to the {page.results.length} loaded results. Load more below to see the rest.</p>}
        {!options.collection && !page.has_exact && <p className="mb-4 text-sm text-neutral-500">{page.count ? "Related terms are shown below." : "Try another name or a known alias."}</p>}
        {view === "compact" ? <div className="space-y-5">{[...groups.values()].filter(group => country === "all" || country === group.id).map(group => <section key={group.id} className="border border-neutral-200 bg-white rounded-xl p-3 sm:p-4 shadow-sm">
            <header className="flex flex-wrap items-center gap-3 mb-3 px-1"><span className="text-xl" aria-hidden="true">{group.flag || "◎"}</span><h2 className="font-semibold text-lg">{group.name}</h2><span className="text-xs text-neutral-500">{group.results.length} results</span>{country === "all" && <button onClick={() => setCountry(group.id)} className="ml-auto text-xs text-violet-700 hover:underline">View all in {group.name} →</button>}</header>
            <ul className="space-y-2">{sorted(group.results).map(result => <CompactResult key={`${result.kind}:${result.id}`} result={result} onOpen={onOpen} options={options} q={q} />)}</ul>
        </section>)}</div> : <ul aria-label="Matching hierarchy paths" className="bg-white border border-neutral-200 rounded-xl p-3 overflow-x-auto">{buildSearchTree(sorted(visible)).map(node => <Branch key={`${node.kind}:${node.id}:${page.results.length}`} node={node} onOpen={onOpen} options={options} />)}</ul>}
        {!page.count && <p className="p-8 bg-white border rounded-xl text-neutral-500">{options.collection ? "No published data matches this collection and the selected filters yet." : "No matching results."}</p>}
        {error && <p role="alert" className="text-red-700 mt-3">{error}</p>}
        {page.next_offset !== null && <button className="btn-secondary mt-4" disabled={busy} onClick={more}>{busy ? "Loading…" : "Load more results"}</button>}
    </div>;
}
